import { useEffect, useState } from 'react'
import { useDispatch,useSelector } from 'react-redux';
import axios from 'axios';
import i18next from 'i18next';

import {setCurrentCompany} from '../store/currentCompanySlice.js'
import {setCurrentBranch} from '../store/currentBranchSlice.js'
import {getCompanyById} from '../utils/tenantManager.js'
import {setSettings} from '../store/settingSlice.js'
import {setLang} from '../store/langSlice.js'

import {arrayNameByLang,arrayToObjKeyValue, appStorageKey} from '../utils/global.js'
import useLicense from './useLicense.js';

const CENTRAL_SETTINGS_SCOPE = 'global';
const CENTRAL_SELECTION_SCOPE = 'header-selection';
const CENTRAL_SETTINGS_CACHE_PREFIX = 'centralSettingsCache:';

const useStartup = () => {
    
    const dispatch = useDispatch();
    const [startup,setStartup] = useState(false);
    const [userSelectionLoaded, setUserSelectionLoaded] = useState(false);
    const user = useSelector(state => state.auth).user
    const lang = useSelector(state => state.lang) 
    const companySelected = useSelector(state => state.currentCompany)
    const branchSelected = useSelector(state => state.currentBranch)

    const safeParse = (value) => {
        if (!value) return null;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };
    const userSettings = arrayToObjKeyValue(safeParse(localStorage.getItem('user'))?.userSettings);
    const [centralLang, setCentralLang] = useState(null);
    const getNameByLang = (nameList, preferredLang = 'en') => {
        const currentLang = String(preferredLang || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
        const fallbackLang = currentLang === 'ar' ? 'en' : 'ar';
        return (
            arrayNameByLang(nameList, currentLang)?.text ||
            arrayNameByLang(nameList, fallbackLang)?.text ||
            nameList?.[0]?.text ||
            ''
        );
    };

    const userCompany = getCompanyById(user?.company_id)
    const storedCompany = safeParse(localStorage.getItem(appStorageKey('selectedCompany')));
    const storedCompanyId = storedCompany?.value;
    const storedCompanyValid = storedCompanyId && getCompanyById(storedCompanyId);
    const selectedCompany = storedCompanyValid || userCompany;
    const selectedUiLang = String(centralLang || lang?.value || userSettings?.DEFAULT_LANG || 'en')
        .toLowerCase()
        .startsWith('ar') ? 'ar' : 'en';
    const selectedCompanyName = selectedCompany && (
        getNameByLang(selectedCompany?.name, selectedUiLang) ||
        'Company'
    );

    const userBranch = userCompany?.branches?.find((branch) => branch?._id === user?.branch_id || branch?.id === user?.branch_id);
    const storedBranch = safeParse(localStorage.getItem(appStorageKey('selectedBranch')));
    const storedBranchId = storedBranch?.value;
    const companyBranches = selectedCompany?.branches || [];
    const storedBranchValid = storedBranchId && companyBranches?.some((branch) => (branch?._id || branch?.id) === storedBranchId);
    const selectedBranch = storedBranchValid
        ? companyBranches?.find((branch) => (branch?._id || branch?.id) === storedBranchId)
        : (selectedCompany?._id === userCompany?._id ? userBranch : companyBranches?.[0]);
    const selectedBranchName = selectedBranch && (
        getNameByLang(selectedBranch?.name, selectedUiLang) ||
        'Branch'
    );

    const invoiceSettings = selectedCompany && arrayToObjKeyValue(selectedCompany?.settings.invoice);
    const generalSettings = selectedCompany && arrayToObjKeyValue(selectedCompany?.settings.generalSettings);
    const quoteSettings = selectedCompany && arrayToObjKeyValue(selectedCompany?.settings.quote);
    const normalizeSettingsBlock = (value) => {
        if (!value) return {};
        if (Array.isArray(value)) return arrayToObjKeyValue(value);
        return value;
    };
    const resolveSettings = (centralValue, fallbackValue) => {
        const normalizedCentral = normalizeSettingsBlock(centralValue);
        if (normalizedCentral && Object.keys(normalizedCentral).length) return normalizedCentral;
        return fallbackValue || {};
    };
    const centralSettingsCacheKey = user?.tenant_id && (selectedCompany?._id || selectedCompany?.id)
        ? `${CENTRAL_SETTINGS_CACHE_PREFIX}${user.tenant_id}:${selectedCompany?._id || selectedCompany?.id}:${CENTRAL_SETTINGS_SCOPE}`
        : null;
    const initialCentralSettings = (() => {
        if (!centralSettingsCacheKey) return null;
        const cachedRaw = safeParse(localStorage.getItem(centralSettingsCacheKey));
        return cachedRaw?.data || null;
    })();
    const [centralSettings, setCentralSettings] = useState(initialCentralSettings);
    const mergedSettings = {
        invoiceSettings: resolveSettings(centralSettings?.invoiceSettings, invoiceSettings),
        generalSettings: resolveSettings(centralSettings?.generalSettings, generalSettings),
        quoteSettings: resolveSettings(centralSettings?.quoteSettings, quoteSettings)
    };
    const DEFAULT_LANG = mergedSettings?.generalSettings?.DEFAULT_LANG || userSettings?.DEFAULT_LANG || 'en';
    
    const {data:dataLicense} = useLicense();

    useEffect(() =>{
        const pathArray = window.location.pathname.split('/');
        if(pathArray.length > 2 && pathArray[pathArray.length-2] === 'token'){
            setStartup(true)
        }
    },[dataLicense]);
    
    useEffect(()=>{
        if(selectedCompany && !userSelectionLoaded){
            const langToApply = lang?.value && lang?.value !== DEFAULT_LANG
                ? lang.value
                : (centralLang || lang?.value || DEFAULT_LANG);
            dispatch(setLang({
                label: langToApply === 'ar' ? 'عربي' : langToApply === 'en' ? 'English' : langToApply,
                value: langToApply
            }))
            i18next.changeLanguage(langToApply);
            
            // Don't set company/branch names here - let useTenant handle it
            // based on the correct language from useLang hook
            // Only set IDs initially if not already set
            if (!companySelected?.value) {
                dispatch(setCurrentCompany({
                    name: 'Company', // Placeholder, useTenant will update this
                    value: selectedCompany._id || selectedCompany?.id
                }));
            }
            if (!branchSelected?.value && selectedBranch?._id) {
                dispatch(setCurrentBranch({
                    name: 'Branch', // Placeholder, useTenant will update this
                    value: selectedBranch?._id || selectedBranch?.id
                }));
            }
    
            dispatch(setSettings({
                invoiceSettings: mergedSettings.invoiceSettings,
                generalSettings: mergedSettings.generalSettings,
                quoteSettings: mergedSettings.quoteSettings
            }));

    
            setStartup(true)
        }

    },[user, selectedCompany?._id, selectedCompany?.id, selectedBranch?._id, selectedBranch?.id, centralSettings, centralLang, lang?.value, userSelectionLoaded, companySelected?.value, branchSelected?.value]);

    useEffect(() => {
        const fetchCentralSettings = async () => {
            if (!(selectedCompany?._id || selectedCompany?.id) || !user?.tenant_id) return;
            const token = localStorage.getItem('token');
            if (!token) return;
            const cacheKey = centralSettingsCacheKey;
            if (!cacheKey) return;
            const cachedRaw = safeParse(localStorage.getItem(cacheKey));
            if (cachedRaw?.data) {
                setCentralSettings(cachedRaw.data);
            }
            const companyId = selectedCompany?._id || selectedCompany?.id;
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${companyId}&scope=${CENTRAL_SETTINGS_SCOPE}`;
            try {
                const headers = {
                    Authorization: `Bearer ${token}`
                };
                if (cachedRaw?.version) {
                    headers['If-None-Match'] = String(cachedRaw.version);
                }
                const response = await axios.get(url, { headers, validateStatus: () => true });
                if (response.status === 304) {
                    return;
                }
                if (response?.data?.status === 1) {
                    const nextData = response?.data?.data || null;
                    const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
                    setCentralSettings(nextData);
                    localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
                }
            } catch (error) {
            }
        };
        fetchCentralSettings();
    }, [selectedCompany?._id, selectedCompany?.id, user?.tenant_id]);

    useEffect(() => {
        const fetchCentralSelection = async () => {
            if (!user?.tenant_id) return;
            const token = localStorage.getItem('token');
            if (!token) return;
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`;
            try {
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true });
                if (response?.data?.status !== 1) {
                    setCentralLang(null);
                    setUserSelectionLoaded(true);
                    return;
                }
                const selection = response?.data?.data;
                const selectedLang = selection?.selectedLang || selection?.lang || selection?.language || response?.data?.currentLanguage;
                setCentralLang(selectedLang || null);
                setUserSelectionLoaded(true);
            } catch (error) {
                setCentralLang(null);
                setUserSelectionLoaded(true);
            }
        };
        fetchCentralSelection();
    }, [user?.tenant_id, user?.company_id, selectedCompany?._id, selectedCompany?.id]);

    return {startup}
}

export default useStartup
